import { Component, Input, NgZone, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng';
import { get, cloneDeep } from 'lodash';
import { ConfirmationService, SelectItem } from 'primeng';
import { BehaviorSubject, Subject } from 'rxjs';
import { OrganisationSpecificStepsService } from '../../services';
import { Message } from '../../classes/Message';
import { ICustomer } from '../../interfaces/customer';
import { NoEmptyString } from '../../interfaces/no-empty-string.validator';
import {
  IOrganisationSpecificStepGroup,
  IOrganisationSpecificSteps,
} from '../../interfaces/organisation-specific-steps';

import { CustomerService } from '../../services';
import { OrganisationsService } from '../../services';
import { TaskTypeService } from '../../services';
import { TaggedLogger } from 'src/app/services/taggedLogger';
import { LoggerService } from 'src/app/services/logger.service';
import { ChecklistService } from '../../services/checklist.service';
import { Loader } from '../../utils/loader';
import { UserService } from '../../services/user.service';
import { IUser } from '../../interfaces/user';
import { RefreshJobEventTypeEnum } from '../../enums/refresh-job-event-type.enum';

@Component({
  selector: 'page-org-specific-steps',
  templateUrl: './organisations-specific-steps.component.html',
  styleUrls: [
    './organisations-specific-steps.component.scss',
    '../checklist-categories/checklist-categories.component.scss',
  ],
})
export class OrganisationSpecificStepsComponent implements OnInit {
  /**
   * The tagged logger
   * @private
   */
  private readonly logger: TaggedLogger;
  /**
   * An async loader
   * @private
   */
  private readonly loader: Loader;
  isUserImpersonating: boolean;
  checklistDisabled: boolean;
  showDisabledChecklists: boolean = false;

  get isBusy() {
    if (!this.loader) return true; // If we've not set this up yet, we're busy

    return this.loader.isBusy;
  }

  /**
   * The customer
   */
  customer: ICustomer;

  /**
   * This is the raw response from getChecklists
   */
  checklists: IOrganisationSpecificStepGroup[];

  newStepForm: UntypedFormGroup;
  name: UntypedFormControl;
  taskType: UntypedFormControl;
  selectedItemId: number;
  editChecklistDialog: boolean = false;
  editStepDialog: boolean = false;
  checklistForm: UntypedFormGroup;
  user: IUser;

  /**
   * todo: what's this used for?
   */
  stepListAltered: Subject<IOrganisationSpecificSteps[]> = new Subject<
    IOrganisationSpecificSteps[]
  >();
  dragEnterTarget: any;
  draggedStep: any;
  isEngineiusUser: boolean;
  newChecklistName: any;
  checklistNameDialog: any;
  selectedCategoryId: any;
  categoryId: any;
  checklistCategorySelect: SelectItem[] = [];
  checklistSubCategorySelect: any;
  departmentSelect: SelectItem[] = [];

  /**
   * The checklist select list options
   */
  checklistSelect: SelectItem[];
  filteredChecklistSelect: SelectItem[];

  handleCloseDialog = (value: any) => (this.editStepDialog = false);

  /**
   * Is this on the checklist admin page?
   * @private
   */
  private _checklistAdmin: boolean;

  @Input('checklistAdmin')
  set checklistAdmin(value: boolean) {
    this._checklistAdmin = value;
  }

  get checklistAdmin(): boolean {
    return this._checklistAdmin;
  }

  /**
   * Which checklist is currently selected in the dropdown?
   * @private
   */
  private _selectedChecklist$ = new BehaviorSubject<IOrganisationSpecificStepGroup>(null);

  set selectedChecklist(checklist: IOrganisationSpecificStepGroup) {
    this._selectedChecklist$.next(checklist);
  }

  get selectedChecklist(): IOrganisationSpecificStepGroup {
    return this._selectedChecklist$.value;
  }

  get selectedChecklist$(): BehaviorSubject<IOrganisationSpecificStepGroup> {
    return this._selectedChecklist$;
  }

  get selectedChecklistId(): number {
    if (!this.selectedChecklist) return null;
    return this.selectedChecklist.id;
  }

  set selectedChecklistId(id: number) {
    this.onChecklistChange(id);
  }

  set selectedChecklistSteps(steps: IOrganisationSpecificSteps[]) {
    this._selectedChecklist$.next({ ...this.selectedChecklist, steps: steps });
  }

  get selectedChecklistSteps(): IOrganisationSpecificSteps[] {
    return get(this.selectedChecklist, 'steps') || [];
  }

  get pickupSteps(): IOrganisationSpecificSteps[] {
    return this.selectedChecklistSteps.filter((step) => step.isPickUp);
  }

  get dropoffSteps(): IOrganisationSpecificSteps[] {
    return this.selectedChecklistSteps.filter((step) => !step.isPickUp);
  }

  /**
   * todo, probably to backing field, and it can take over the mess below
   * The currently selected step?
   */
  selectedStep: IOrganisationSpecificSteps;
  checklistSubCategoryIds?: number[];

  /**
   * The currently selected pickup step in the table
   * @todo this isn't really doing a whole lot, it adds an unused css class to the table.
   */
  private _selectedPickupStepId: number = null;
  get selectedPickupStepId(): number {
    return this._selectedPickupStepId;
  }

  set selectedPickupStepId(id: number) {
    this.logger.info('Selected Pickup Step changed to', id);
    this._selectedPickupStepId = id;
  }

  /**
   * The currently selected dropoff step in the table
   * @todo this isn't really doing a whole lot, it adds an unused css class to the table.
   */
  private _selectedDropoffStepId: number = null;
  infoUrlLinks: any;

  get selectedDropoffStepId(): number {
    return this._selectedDropoffStepId;
  }

  set selectedDropoffStepId(id: number) {
    this.logger.info('Selected Dropoff Step changed to', id);
    this._selectedDropoffStepId = id;
  }

  /**
   * The name of the selected checklist
   */
  get checklistCategoryName(): string {
    if (!get(this.selectedChecklist, 'categoryId')) return '';

    const selectedChecklist = this.checklistCategorySelect.find(
      (x) => x.value == this.selectedChecklist.categoryId,
    );
    return selectedChecklist ? selectedChecklist.label : '';
  }

  /**
   * This is the checklist name on the form (add/edit checklist)
   */
  get formChecklistName(): string {
    const checklistName = get(this.checklistForm, 'value.groupName');
    if (!checklistName) return '';

    return `${checklistName}`.trim();
  }

  get editingChecklistIsDefault(): boolean {
    const editingChecklistId = this.checklistForm?.controls.id?.value;
    if (editingChecklistId == null) return false;

    return !!this.checklists?.find((checklist) => checklist.id == editingChecklistId)?.isDefault;
  }

  constructor(
    private customerService: CustomerService,
    private checklistService: ChecklistService,
    private organisationsService: OrganisationsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private formBuilder: UntypedFormBuilder,
    private organisationSpecificStepService: OrganisationSpecificStepsService,
    private loggerService: LoggerService,
    private userService: UserService,
    private zone: NgZone,
  ) {
    const tag = 'component:organisation-specific-steps';
    this.logger = loggerService.taggedLogger(tag);
    this.loader = Loader.create(tag, loggerService);
    this.checklistForm = formBuilder.group({
      name: ['', [Validators.maxLength(255)]],
    });
    this.name = new UntypedFormControl(
      '',
      Validators.compose([Validators.required, Validators.maxLength(250), NoEmptyString]),
    );
    this.taskType = new UntypedFormControl(null, null);
    this.newStepForm = new UntypedFormGroup({
      name: this.name,
    });
    this.checklistForm = this.buildFormValues(null);

    this.userService.isUserImpersonating().then((result) => {
      if (result != null) this.isUserImpersonating = true;
    });

    this.user = this.userService.currentUser;
  }

  async ngOnInit() {
    await this.loadChecklists();
    this.initChecklistCategory();
    this.loadDepartments();
  }

  private async loadChecklists(): Promise<void> {
    try {
      await Promise.all([this.setCurrentCustomer(), await this.getChecklist()]);
    } catch (error) {
      this.logger.error('Could not load checklists');
      // todo: should proabably do something here to let the user know
    }
  }

  private initChecklistCategory() {
    this.checklistCategorySelect = this.checklistService.ChecklistCategoryName.map((k, v) => {
      return { value: v, label: k };
    });

    this.checklistService
      .getChecklistSubCategories({
        isActive: true,
        component: 'organisations-specfic-steps',
      })
      .then((x) => {
        this.checklistSubCategorySelect = [];

        x.forEach((subCat) => {
          this.checklistSubCategorySelect.push({
            label: subCat.checklistCategory.title + ': ' + subCat.title,
            value: subCat.id,
          });
        });
      });
  }

  private loadDepartments(): void {
    try {
      this.userService.getCurrentUserOrg().then((data) => {
        this.departmentSelect = [];
        this.departmentSelect = [
          { label: 'Global', value: null },
          ...data.departments.map((dept: any) => ({ label: dept.name, value: dept.id })),
        ];
      });
    } catch (error) {
      this.logger.error('Could not load departments', error);
    }
  }

  private buildFormValues(checklist: IOrganisationSpecificStepGroup): UntypedFormGroup {
    return this.formBuilder.group({
      id: new UntypedFormControl(checklist ? checklist.id : null, null),
      groupName: new UntypedFormControl(
        checklist ? checklist.groupName : null,
        Validators.compose([Validators.required, NoEmptyString]),
      ),

      description: new UntypedFormControl(checklist ? checklist.description : null, null),
      customerDescription: new UntypedFormControl(
        checklist ? checklist.customerDescription : null,
        null,
      ),
      supplierDescription: new UntypedFormControl(
        checklist ? checklist.supplierDescription : null,
        null,
      ),
      customerDisclaimer: new UntypedFormControl(
        checklist ? checklist.customerDisclaimer : null,
        null,
      ),
      checklistSubCategoryIds: new UntypedFormControl(
        checklist
          ? checklist.orgStepChecklistSubCategories.map((x) => x.checklistSubCategoryId)
          : [],
        null,
      ),
      infoURL: new UntypedFormControl(checklist ? checklist.infoURL : '', [
        Validators.pattern('((https?:\\/\\/)[^\\s]+)'),
      ]),
      customerDepartmentId: new UntypedFormControl(
        checklist ? checklist.customerDepartmentId : null,
        null,
      ),
    });
  }

  /**
   * Creates a new checklist dialog
   */
  newCheckList() {
    this.logger.info('New Checklist');
    this.editChecklistDialog = true;
    this.checklistForm = this.buildFormValues(null);
  }

  /**
   * Creates the edit checklist dialog
   */
  editChecklist() {
    if (!this.selectedChecklistId) return;

    this.logger.info('Edit Checklist', this.selectedChecklistId);
    this.editChecklistDialog = true;
    this.checklistForm = this.buildFormValues(this.selectedChecklist);
  }

  /**
   * Creates a new checklist (on form button click)
   * @param checklist The checklist
   * @return resolves when done
   */
  async addChecklist(checklist: IOrganisationSpecificStepGroup): Promise<void> {
    // todo: might need to come back to this condition, seems like side effect
    if (checklist) {
      checklist.isDefault = !this.checklistSelect || this.checklistSelect.length == 0;
      checklist.organisationId = this.customer.id;
    }

    // if an inactive version of the checklist exists, prompt user to reinstate it
    const hasBeenDeletedResult = await this.organisationsService.hasChecklistBeenDeleted(checklist);
    if (hasBeenDeletedResult) {
      this.confirmReinstateChecklist(checklist);
      return;
    }
    try {
      this.zone.run(() => {
        this.editChecklistDialog = false;
      });
      const addChecklistResult = await this.organisationsService.addChecklist(checklist);
      await this.getChecklist();
      this.zone.run(() => {
        if (addChecklistResult) this.selectedChecklistId = addChecklistResult;
      });
    } catch (error) {
      this.logger.error('Could not add checklist', error);
      this.messageService.add(Message.Error(error.error.Message, 'Error'));
    }
  }

  private confirmReinstateChecklist(checklist: IOrganisationSpecificStepGroup) {
    try {
      this.logger.info('Showing confirmation: would you like to reinstate the checklist?');
      this.confirmationService.confirm({
        message: 'This checklist exists but has been deleted, would you like to reinstate it?',
        header: 'Confirmation',
        accept: () => this.reinstateChecklist(checklist),
        reject: () => this.logger.info('User decided not to reinstate the existing checklist'),
      });
    } catch (error) {
      this.logger.error('Error showing confirm', error);
    }
  }

  private async reinstateChecklist(checklist: IOrganisationSpecificStepGroup) {
    try {
      // reinstate the checklist
      this.logger.info('User is reinstating the checklist', checklist);
      await this.organisationsService.reinstateChecklist(checklist);
      this.editChecklistDialog = false;
      await this.getChecklist();
      this.logger.debug('Checklist reinstated');
    } catch (error) {
      this.logger.error('Could not reinstate checklist', error);
      // todo: warn the user somehow.
    }
  }

  /**
   * Saves an updated checklist
   * @param checklist The updated checklist (form values)
   * @return resolves when done
   */
  async saveChecklist(checklist) {
    try {
      this.zone.run(() => {
        this.editChecklistDialog = false;
      });
      const grp = this.checklists.find((checklist) => checklist.id === this.selectedChecklistId);

      // save checklist group
      const editObj = {
        groupName: checklist.groupName,
        customerDescription: checklist.customerDescription,
        customerDisclaimer: checklist.customerDisclaimer,
        supplierDescription: checklist.supplierDescription,
        infoURL: checklist.infoURL,
        categoryId: checklist.categoryId,
        checklistSubCategoryIds: checklist.checklistSubCategoryIds,
        customerDepartmentId: checklist.customerDepartmentId,
        organisationId: this.customer.id,
        isDefault: grp.isDefault,
        description: checklist.description,
        id: checklist.id,
      };

      await this.organisationsService.updateChecklist(editObj);
      await this.getChecklist();

      this.organisationSpecificStepService
        .getChecklistSteps(this.selectedChecklist.id)
        .then((res) => {
          if (res) {
            this.selectedChecklist.steps = res;
          }
        });
    } catch (error) {
      this.logger.error('Could not save checklist', error);
      this.messageService.add(Message.Error(error.error.Message, 'Error'));
      // todo: should probably let the user know
    }
  }

  /**
   * Shows a confirm dialog to delete the selected checklist
   */
  confirmDeleteChecklist() {
    if (!this.selectedChecklist) {
      this.logger.warn('Delete checklist clicked but no selected checklist was found');
      return;
    }

    this.confirmationService.confirm({
      message: `Confirm you want to delete Checklist '${this.selectedChecklist.groupName}'?`,
      accept: () => this.deleteChecklist(),
      reject: () => this.logger.info('User selected to not delete checklist'),
    });
  }

  /**
   * Deletes the selected checklist, if the confirm dialog has been accepted
   * @private
   */
  private async deleteChecklist() {
    try {
      this.logger.info('User chose to delete checklist', this.selectedChecklistId);
      await this.organisationsService.deleteChecklist(this.selectedChecklistId);
      // todo, selectedGroupId and selectedchecklist seem related, could be another getter?
      this.zone.run(() => {
        this.selectedChecklist = null;
        this.selectedChecklistId = 0;
      });
      await this.getChecklist();
    } catch (error) {
      this.logger.error('Could not delete checklist', this.selectedChecklistId, error);
      // todo: should probably let the user know.
    }
  }

  /**
   * Sets a Checklist as the default checklist
   * @return resolves when done
   */
  async setSelectedChecklistAsDefault(confirmed: boolean): Promise<void> {
    if (this.selectedChecklist?.customerDepartmentId) {
      const proceed = await this.confirmChangeToGlobal(
        this.selectedChecklist.customerDepartmentName,
      );
      if (!proceed) {
        return;
      }
    }

    const newDefaultChecklistId = this.selectedChecklistId;

    try {
      this.logger.info(
        'Setting selected checklist as the default checklist',
        this.selectedChecklistId,
      );
      // todo: feels fishy (side affect)
      const grp = this.checklists.find((x) => x.id == this.selectedChecklistId);
      grp.isDefault = true;

      const checklist = {
        id: this.selectedChecklistId,
        organisationId: this.customer.id,
        checklistSubCategoryIds: grp.orgStepChecklistSubCategories.map(
          (x) => x.checklistSubCategoryId,
        ),
        groupName: grp.groupName,
        infoURL: grp.infoURL,
        isDefault: grp.isDefault,
      };

      this.organisationsService.updateChecklist(checklist).then(async () => {
        await this.loadChecklists();
        await this.initChecklistCategory();
        this.selectedChecklistId = newDefaultChecklistId;
      });
    } catch (error) {
      this.logger.error('Could not set selected checklis as default', error);
    }
  }

  private confirmChangeToGlobal(deptName: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        message: `Please note that this checklist is linked to a department (${deptName}). If you change the default checklist, the department will be reset to Global. Are you sure you want to proceed?`,
        acceptLabel: 'Continue',
        rejectLabel: 'Cancel',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  /**
   * Reorer the step that has been dropped
   * todo: this is likely the bit I'm looking at messing with. COME BACK HERE
   * @param pickup If true it's the pickup selected step? todo: why?
   */
  async stepOrderChanged(pickup: boolean) {
    this.logger.info('User dropped a step(reorder)', pickup, event);
    const steps = pickup ? this.pickupSteps : this.dropoffSteps;
    const taskId = this.loader.addTask(`stepOrderChanged(${pickup})`);

    try {
      // todo: nope.
      for (let i = 0; i < steps.length; i++) {
        steps[i].order = i;
      }

      await this.organisationsService.updateSpecificStepsList(steps);
    } catch (error) {
      this.logger.error('Could not change order of steps', error);
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  addNewStep(isPickup: boolean) {
    this.selectedStep = <IOrganisationSpecificSteps>{
      organisationSpecificStepsGroupId: this.selectedChecklistId,
      groupName: this.selectedChecklist ? this.selectedChecklist.groupName : null,
      isPickUp: isPickup,
      organisationId: this.customer.id,
    };
    this.editStepDialog = true;
  }

  editChecklistItem(item: IOrganisationSpecificSteps) {
    this.selectedItemId = item.id;
    // EN-3287: edit a deep copy so ticking Required / Allow Photos, editing answer
    // text, etc. only take effect when the user clicks Save. Previously the dialog
    // shared the grid's object by reference, so edits stuck even after Cancel.
    this.selectedStep = cloneDeep(item);
    this.editStepDialog = true;
  }

  /**
   * Deletes a given step by id
   * @param stepId The step id
   * @param pickup Is it a pickup step?
   */
  async deleteStep(stepId: number, pickup: boolean): Promise<void> {
    const stepsLength = this.selectedChecklistSteps.length;

    if (stepsLength <= 1) {
      this.logger.warn('Cannot delete the last step in a checklist');
      this.messageService.add(Message.Error('Cannot delete the last step in a checklist.'));
      return;
    }

    //Delete on server
    const taskId = this.loader.addTask(`deleteStep(${stepId}, ${pickup})`);
    try {
      const response = await this.organisationsService.deleteSpecificStep(stepId, true);

      // todo: seems like a side effect
      response.sort((a, b) => {
        return a.order - b.order;
      });

      this.refreshSteps(response);

      this.selectedChecklistSteps = <IOrganisationSpecificSteps[]>response;
    } catch (error) {
      this.logger.error('Could not delete step', error);
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  /**
   * Gets checklists from the organisation service
   * Sets:
   * - groupsData
   * - customer.id?
   * - checklistSelect (Option group for the select lists)
   *
   */
  private async getChecklist(): Promise<void> {
    const taskId = this.loader.addTask('getChecklist()');

    try {
      const response = await this.organisationsService.getChecklists(false);

      const mappedToSelectItems = response.map((checklist) => ({
        title: checklist.groupName,
        label:
          `${checklist.groupName}` +
          (checklist.isDefault
            ? ` (Default)`
            : checklist.inactiveDateTime != null
            ? ' (Disabled)'
            : ''),
        value: checklist.id,
      }));

      this.zone.run(() => {
        this.checklists = response;
        this.checklistSelect = mappedToSelectItems;
        this.filteredChecklistSelect = this.showDisabledChecklists
          ? this.checklistSelect
          : this.checklistSelect.filter((item) => !item.label?.includes('(Disabled)'));
      });

      if (this.selectedChecklistId) {
        this.selectedChecklist = response.find(
          (checklist) => checklist.id == this.selectedChecklistId,
        );

        this.setWorkflowLink(this.selectedChecklist);
      } else {
        const defaultChecklist = this.checklists.find((checklist) => checklist.isDefault);
        if (defaultChecklist) {
          this.selectedChecklistId = defaultChecklist.id;
          this.setWorkflowLink(defaultChecklist);
        } else if (this.checklists.length > 0) {
          // If there's no default, select the first in the list.
          this.selectedChecklistId = this.checklists[0].id;
          this.setWorkflowLink(this.checklists[0]);
        }
      }
    } catch (error) {
      this.logger.error('Could not get checklists', error);
      // todo: should probably let the user know
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  private setWorkflowLink(checklist: IOrganisationSpecificStepGroup) {
    this.infoUrlLinks = [
      {
        label: 'View Workflow',
        icon: 'fa fa-external-link',
        command: () => {
          this.visitURL(checklist.infoURL);
        },
      },
    ];
  }

  /**
   * Set the current customer?
   * On what?
   * @private
   */
  private async setCurrentCustomer(): Promise<void> {
    const taskId = this.loader.addTask('setCurrentCustomer()');
    try {
      const customer = await this.customerService.GetCurrent();
      if (!customer) {
        throw new Error('Customer was falsy on return');
      }
      this.customer = customer;
      this.isEngineiusUser = this.customer.organisationTypeId == 1;
    } catch (error) {
      this.logger.error('Could not set current customer', error);
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  private refreshSteps(steps: IOrganisationSpecificSteps[]) {
    let sorted = steps.sort((a, b) => {
      return a.order - b.order;
    });
    this.zone.run(() => {
      this.selectedChecklistSteps = sorted;
    });

    /**
     * todo: remove this, convert to async pipe
     * i.e. You shouldnt' need to notify the child components via an observable.
     */
    this.stepListAltered.next(sorted); // this will notify all child components of the changes
  }

  /**
   * On checklist change, we get the steps for a checklist
   */
  async onChecklistChange(id: number): Promise<void> {
    this.logger.info(`onChecklistChange(${id})`);

    if (!this.checklists) {
      this.logger.debug(`no checklist data yet, shortcutting`);
      return;
    }

    const taskId = this.loader.addTask(`onChecklistChange(${id})`);
    try {
      if (id) {
        this.selectedChecklist = this.checklists.find((checklist) => checklist.id == id);
        this.checklistDisabled = this.selectedChecklist.inactiveDateTime != null;

        const steps = await this.organisationSpecificStepService.getChecklistSteps(id);
        if (steps) {
          this.refreshSteps(steps);
        }
        this.selectedChecklistSteps = steps;
      }
    } catch (error) {
      this.logger.error('Could not load new checklist', id, error);
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  /**
   * Unlocks a checklist
   */
  async unlockChecklist(): Promise<void> {
    this.logger.info('Unlock Checklist', this.selectedChecklistId);
    const taskId = this.loader.addTask(`unlockChecklist(${this.selectedChecklistId})`);
    try {
      const response = await this.organisationSpecificStepService.unlockChecklist(
        this.selectedChecklist,
      );
      if (!response) throw new Error('Response from unlock checklist was falsy');
      await this.loadChecklists();
    } catch (error) {
      this.logger.error('Could not unlock checklist', error);
      // todo: probably warn the user
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  /**
   * Duplicates the currently selected checklist
   */
  async duplicateChecklist(): Promise<void> {
    this.logger.info('Duplicating checklist', this.selectedChecklistId, this.newChecklistName);
    const taskId = this.loader.addTask(
      `duplicateChecklist(${this.selectedChecklistId}, ${this.newChecklistName})`,
    );
    try {
      const response = await this.organisationSpecificStepService
        .createChecklistClone(
          this.selectedChecklist,
          this.customer.id,
          false,
          this.newChecklistName,
        )
        .catch((error) => {
          console.log('ERROR: ', error);
        });

      if (!response) throw new Error('Duplicate Checklist Server response was falsy');

      // Refresh list of checklists
      await this.getChecklist();

      // This forces an update of the dropdown select
      this.selectedChecklistId = response.id;

      this.checklistNameDialog = false;

      this.logger.debug('Duplicated checklist');
    } catch (error) {
      this.logger.error('Could not duplicate checklist', this.selectedChecklistId, error);
      // todo: probably warn the user
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  /**
   * True if the checklist name already exists, used to display validation error on the add/edit checklist form.
   */
  checklistNameExists(): boolean {
    const checklistName = this.formChecklistName;

    if (this.checklistSelect == null || this.checklistSelect.length == 0) {
      return false;
    }

    const editingChecklistId = this.checklistForm?.controls.id?.value;

    const exists = this.checklistSelect.find(
      (checklist) =>
        checklist.title?.trim().toLowerCase() == checklistName.toLowerCase() &&
        (editingChecklistId == null || checklist.value != editingChecklistId),
    );

    return !!exists;
  }

  /**
   * When the checklist is updated we need to reload items?
   * @todo Ohhh, looks like this is actually on new step.
   */
  async handleChecklistStepUpdated(): Promise<void> {
    const taskId = this.loader.addTask('handleChecklistStepUpdated()');
    try {
      if (this.selectedChecklistId) {
        const response = await this.organisationSpecificStepService.getChecklistSteps(
          this.selectedChecklistId,
        );
        if (!response)
          throw new Error('Response from getChecklistSteps was falsy, after step update');
        this.refreshSteps(response);
        this.editStepDialog = false;
      }
    } catch (error) {
      this.logger.error('Could not update checklist steps');
      // todo: let the user know
    } finally {
      this.loader.completeTask(taskId);
    }
  }

  visitURL(infoURL: string) {
    window.open(infoURL, '_blank');
  }

  canDuplicate() {
    return true;

    // See fix/#1923 - only Engineius users that see this option to create/edit checklists so the roles shouldn’t really be considered.
    // const roles = ['CUSA', 'AADM'];
    // return this.user.roles.filter((x) => x.isBelong && roles.includes(x.name)).length > 0;
  }

  displayDisabledChecklists() {
    this.filteredChecklistSelect = this.showDisabledChecklists
      ? this.checklistSelect
      : this.checklistSelect.filter((item) => !item.label?.includes('(Disabled)'));
  }
}
